/**
 * Hand stability analysis and guidance system
 * Helps users maintain steady hand positions for better gesture recognition
 */

export class HandStabilityAssistant {
  private stabilityHistory: number[] = [];
  private readonly MAX_HISTORY = 10;
  private stabilityThreshold = 0.02; // Movement threshold for stability
  private stabilityScore = 0;
  private lastStablePosition: number[][] | null = null;

  /**
   * Analyze hand stability based on landmark movement
   */
  analyzeStability(landmarks: number[][][]): {
    isStable: boolean;
    stabilityScore: number;
    feedback: string;
    guidePosition?: { x: number; y: number };
  } {
    if (landmarks.length === 0 || !landmarks[0]) {
      return { isStable: false, stabilityScore: 0, feedback: 'Positioniere deine Hand in der Kamera' };
    }

    const hand = landmarks[0];
    if (hand.length < 21) {
      return { isStable: false, stabilityScore: 0, feedback: 'Halte deine Hand ruhig' };
    }

    // Calculate center of palm as reference point
    const palmCenter = this.calculatePalmCenter(hand);
    const movement = this.lastStablePosition
      ? this.calculateMovement(this.lastStablePosition, palmCenter)
      : 0;

    // Update stability history
    this.stabilityHistory.push(movement);
    if (this.stabilityHistory.length > this.MAX_HISTORY) {
      this.stabilityHistory.shift();
    }

    // Calculate stability score (lower movement = higher stability)
    const avgMovement = this.stabilityHistory.reduce((sum, m) => sum + m, 0) / this.stabilityHistory.length;
    this.stabilityScore = Math.max(0, 1 - (avgMovement / this.stabilityThreshold));

    const isStable = this.stabilityScore > 0.7;

    if (isStable) {
      this.lastStablePosition = palmCenter;
    }

    let feedback = '';
    let guidePosition: { x: number; y: number } | undefined;

    if (!isStable) {
      if (this.stabilityScore < 0.3) {
        feedback = 'Halte deine Hand ruhiger';
        guidePosition = { x: 0.5, y: 0.5 }; // Center of screen
      } else if (this.stabilityScore < 0.7) {
        feedback = 'Fast geschafft! Halte still';
      }
    } else {
      feedback = 'Perfekt! Hand ist stabil';
    }

    return {
      isStable,
      stabilityScore: this.stabilityScore,
      feedback,
      ...(guidePosition ? { guidePosition } : {})
    };
  }

  /**
   * Calculate center of palm using key landmarks
   */
  private calculatePalmCenter(hand: number[][]): number[][] {
    // Use wrist and base of fingers as reference
    const wrist = hand[0];
    const indexBase = hand[5];
    const pinkyBase = hand[17];

    if (!wrist || !indexBase || !pinkyBase ||
        wrist[0] === undefined || wrist[1] === undefined || wrist[2] === undefined ||
        indexBase[0] === undefined || indexBase[1] === undefined || indexBase[2] === undefined ||
        pinkyBase[0] === undefined || pinkyBase[1] === undefined || pinkyBase[2] === undefined) {
      return [[0, 0, 0]];
    }

    const centerX = (wrist[0] + indexBase[0] + pinkyBase[0]) / 3;
    const centerY = (wrist[1] + indexBase[1] + pinkyBase[1]) / 3;
    const centerZ = (wrist[2] + indexBase[2] + pinkyBase[2]) / 3;

    return [[centerX, centerY, centerZ]];
  }

  /**
   * Calculate movement between two positions
   */
  private calculateMovement(pos1: number[][], pos2: number[][]): number {
    const p1 = pos1[0];
    const p2 = pos2[0];
    if (!p1 || !p2) return 0;
    if (p1[0] === undefined || p1[1] === undefined || p1[2] === undefined ||
        p2[0] === undefined || p2[1] === undefined || p2[2] === undefined) return 0;

    const dx = p1[0] - p2[0];
    const dy = p1[1] - p2[1];
    const dz = p1[2] - p2[2];

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Reset stability tracking
   */
  reset(): void {
    this.stabilityHistory = [];
    this.stabilityScore = 0;
    this.lastStablePosition = null;
  }

  /**
   * Get current stability status
   */
  getStabilityStatus(): { score: number; isStable: boolean } {
    return {
      score: this.stabilityScore,
      isStable: this.stabilityScore > 0.7
    };
  }

  /**
   * Set stability threshold
   */
  setStabilityThreshold(threshold: number): void {
    this.stabilityThreshold = Math.max(0.01, Math.min(0.1, threshold));
  }

  /**
   * Get stability statistics
   */
  getStabilityStats(): {
    currentScore: number;
    averageMovement: number;
    historySize: number;
    threshold: number;
  } {
    const avgMovement = this.stabilityHistory.length > 0
      ? this.stabilityHistory.reduce((sum, m) => sum + m, 0) / this.stabilityHistory.length
      : 0;

    return {
      currentScore: this.stabilityScore,
      averageMovement: avgMovement,
      historySize: this.stabilityHistory.length,
      threshold: this.stabilityThreshold
    };
  }
}